import uuid from "uuid";

import {
  AllowableMimeType,
  AllowableFileExtension,
  FileUpload,
  Classroom,
  Resource,
  ResourceFile,
} from "@examspark/types";

export { default as sanitize } from "sanitize-filename";
// export { default as parse } from "parse-filepath";
export { default as uuid } from "uuid";

export const mimeTypeToResourceTypeMap: Partial<
  Record<AllowableMimeType, string>
> = {
  "application/pdf": "DOCUMENT",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "SPREADSHEET",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCUMENT",
  "audio/mp3": "AUDIO",
  "audio/ogg": "AUDIO",
  "image/gif": "IMAGE",
  "image/jpeg": "IMAGE",
  "image/png": "IMAGE",
  "image/svg": "IMAGE",
  "text/csv": "SPREADSHEET",
  "video/mp4": "VIDEO",
  "video/webm": "VIDEO",
};

export const allowedFileUploadRasterImageMimeTypes: AllowableMimeType[] = [
  "image/gif",
  "image/jpeg",
  "image/png",
];

export const allowedFileUploadImageMimeTypes: AllowableMimeType[] = [
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg",
];

export const allowedFileUploadDocumentMimeTypes: AllowableMimeType[] = [
  "application/pdf",
];

export const allowedFileUploadAudioMimeTypes: AllowableMimeType[] = [
  "audio/mp3",
  "audio/mpeg",
];

export const allowedFileUploadVideoMimeTypes: AllowableMimeType[] = [
  "video/mp4",
  "video/mp4",
];

export const allowedFileUploadMimeTypes: AllowableMimeType[] = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/zip",
  "audio/mp3",
  "audio/mpeg",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg",
  "text/csv",
  "video/mp4",
  "video/mp4",
];

export const allowedFileUploadExtensions: AllowableFileExtension[] = [
  ".ai",
  ".svg",
];

export const getSharedMediaTypeByMimeType = (mimetype: AllowableMimeType) => {
  const type =
    mimeTypeToResourceTypeMap[
      mimetype as keyof typeof mimeTypeToResourceTypeMap
    ];

  if (type) {
    return type as keyof typeof mimeTypeToResourceTypeMap;
  }

  return "OTHER";
};

export const newFileUpload = (file: File) => ({
  file,
  progress: {
    loaded: 0,
    total: 0,
  },
  type: "file",
  status: "pending",
});

export const getMimeTypesForResource = (data?: Resource) => {
  const accept: AllowableMimeType[] = [];
  switch (data?.type) {
    case "IMAGE":
      accept.push(...allowedFileUploadImageMimeTypes);
      break;
    case "DOCUMENT":
      accept.push(...allowedFileUploadDocumentMimeTypes);
      break;
    case "AUDIO":
      accept.push(...allowedFileUploadAudioMimeTypes);
      break;
    case "VIDEO":
      accept.push(...allowedFileUploadVideoMimeTypes);
      break;
  }

  return accept;
};

export const getUploadKeyForNewResource = (
  file: FileUpload,
  workspaceId: string,
) => {
  const resourceId = uuid.v4();
  const formattedCreationDate = new Date()
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, "");

  const fileName = file.file.name;
  const fileKey = [
    "workspace",
    workspaceId,
    "resources",
    formattedCreationDate,
    resourceId,
    fileName,
  ].join("/");

  return {
    resourceId,
    key: fileKey,
  };
};

export const getS3KeyPrefixForClassroom = (
  workspaceId: string,
  classroom: Classroom,
) => {
  if (!classroom.createdAt)
    throw new Error("Classroom does not have a creation date");

  const formattedCreationDate = new Date(classroom.createdAt)
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, "");

  const fileKey = [
    "workspace",
    workspaceId,
    "classrooms",
    formattedCreationDate.substring(0, 4),
    formattedCreationDate.substring(4),
    classroom.id,
  ].join("/");

  return fileKey;
};

export const getS3KeyPrefixForExamBlobs = (
  workspaceId: string,
  examConfigYear: string,
  examConfigMonth: string,
  examConfigId: string,
  examAttemptId: string,
  questionId: string,
  interactionId: string,
  blobId: string,
) => {
  const examBasePath = [
    "public",
    "workspace",
    workspaceId,
    "exams",
    examConfigYear,
    examConfigMonth,
    examConfigId,
    examAttemptId,
  ];
  const fileKey = [...examBasePath, questionId, interactionId, blobId].join(
    "/",
  );

  return fileKey;
};

export const parseS3UrlForResourceFile = (
  url: string,
): Pick<ResourceFile, "bucket" | "region" | "key" | "name" | "extension"> => {
  const parsedUrl = new URL(url);

  const region = parsedUrl.hostname.split(".")[2];
  const bucket = parsedUrl.hostname.split(".")[0];
  const key = decodeURIComponent(parsedUrl.pathname.slice(1));
  const name = key.split("/").pop() || "";
  let extension = undefined;
  if (name.includes(".")) {
    extension = name.split(".").pop();
  }

  return {
    bucket,
    region,
    key,
    name,
    extension,
  };
};
